const findSumToIndex = (arr: Array<number>, from = 0, to = arr.length) =>
    arr.slice(from, to).reduce((sum, value) => sum + value, 0);

export function splitByPoints<T>(arr: Array<T>, points: Array<number>): Array<Array<T>> {
    let result: T[][] = [];

    for (let i = 0; i <= points.length; i += 1) {
        const startIndex = i === 0 ? 0 : points[i - 1];
        const endIndex = i === points.length ? arr.length : points[i];

        const chunk = arr.slice(startIndex, endIndex);

        if (chunk.length) {
            result.push(arr.slice(startIndex, endIndex));
        }
    }

    return result;
}

export const getBestSplitPoints = (arr: Array<number>, numberChunks: number): Array<number> => {
    if (numberChunks <= 1 || arr.length <= 1) {
        return arr;
    }

    if (arr.length <= numberChunks + 1) {
        const length = arr.length === numberChunks + 1 ? arr.length - 2 : arr.length - 1;
        return Array.from({ length }, (_, i) => i + 1);
    }

    const sum = findSumToIndex(arr);
    const avgMaxLength = Math.floor(sum / numberChunks);
    const maxChunkLength = getMaxChunkLength(arr, sum, numberChunks);

    const pointsByAvgMiddle = getPointsByMaxLen(arr, avgMaxLength, numberChunks);
    const pointsByMaxChunkLength = getPointsByMaxLen(arr, maxChunkLength, numberChunks);

    const avgDelta = getDeltaFromPoints(arr, pointsByAvgMiddle, avgMaxLength);
    const maxChunkDelta = getDeltaFromPoints(arr, pointsByMaxChunkLength, avgMaxLength);

    return avgDelta < maxChunkDelta ? pointsByAvgMiddle : pointsByMaxChunkLength;
};

const getDeltaFromPoints = (arr: Array<number>, points: Array<number>, middle: number): number => {
    const chunks = splitByPoints(arr, points);
    const partsSum = chunks.map((chunk) => findSumToIndex(chunk));

    return Math.max(...partsSum.map((partSum) => Math.abs(partSum - middle)));
};

export const getMaxChunkLength = (arr: Array<number>, sum: number, numberChunks: number): number => {
    let startSum = Math.max(...arr);
    let endSum = sum;

    while (startSum <= endSum) {
        let midSum = Math.floor(startSum + (endSum - startSum) / 2);
        let numberOfSplits = 1;
        let currSum = 0;

        for (let num of arr) {
            currSum += num;
            if (currSum > midSum) {
                numberOfSplits += 1;
                currSum = num;
            }
        }

        if (numberOfSplits > numberChunks) {
            startSum = midSum + 1;
        } else {
            endSum = midSum - 1;
        }
    }

    return startSum;
};

export const getPointsByMaxLen = (arr: Array<number>, maxLength: number, numberChunks: number): Array<number> => {
    const points: number[] = [];
    let splitIndex = 0;

    for (let i = splitIndex; i < arr.length; i += 1) {
        if (findSumToIndex(arr, splitIndex, i + 1) > maxLength) {
            if (i !== 0) {
                points.push(i);
            }
            splitIndex = i;
        }
    }

    return fitPointsToNumberChunks(points, splitIndex, numberChunks);
};

export const fitPointsToNumberChunks = (
    points: Array<number>,
    splitIndex: number,
    numberChunks: number
): Array<number> => {
    const fittedPoints = [...points];

    while (numberChunks - fittedPoints.length > 0) {
        splitIndex += 1;
        fittedPoints.push(splitIndex);
    }

    while (fittedPoints.length >= numberChunks) {
        fittedPoints.pop();
    }

    return fittedPoints;
};
